import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link, CheckCircle2, Users, Clock, Calendar as CalendarIcon, Bell, X } from 'lucide-react'
import { FormModal } from '@components/branded-modal'
import { AdasBox } from '@components/wrapper-components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { createCalendarPreferences } from 'api/api'
import { Logger } from 'logger'
import { useCenterStore } from '@caradasstore/CenterStore'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { cn } from 'lib/utils'

dayjs.extend(duration)

const techniciansOptions = [
  { value: 1, label: '1 Technician' },
  { value: 2, label: '2 Technicians' },
  { value: 3, label: '3 Technicians' },
  { value: 4, label: '4 Technicians' },
  { value: 5, label: '5 Technicians' },
  { value: 6, label: '6 Technicians' }
]
const minutesOpts = (vals) => vals.map((v) => ({ value: v, label: `${v} Minutes` }))
const durationOptions = minutesOpts([30, 60, 90, 120])
const timeIntervalOptions = minutesOpts([30, 60, 90])
const timeGapOptions = minutesOpts([0, 15, 30, 45, 60])
const daysOptions = [
  { value: 14, label: '14 Days' },
  { value: 21, label: '21 Days' }
]
const minRescheduleOptions = [
  { value: 0, label: '0 Minutes' },
  { value: 30, label: '30 Minutes' },
  { value: 60, label: '60 Minutes' },
  { value: 90, label: '90 Minutes' }
]

const PreferenceRow = ({ icon: Icon, title, required, description, children, isLast }) => (
  <div className={cn('flex flex-wrap items-start gap-4 px-4 py-4 sm:flex-nowrap sm:items-center', !isLast && 'border-b border-[#EBEDF0]')}>
    <div className='flex flex-1 items-start gap-3 min-w-0'>
      <span className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-md bg-[#F1F4F8]'>
        <Icon className='h-4 w-4 text-[#0A1628]' />
      </span>
      <div className='min-w-0'>
        <div className='text-[14px] font-bold text-[#0A1628]'>
          {title}
          {required && <span className='ml-1 text-[#DC2626]'>*</span>}
        </div>
        {description && <div className='mt-0.5 text-[12px] text-[#6B7280]'>{description}</div>}
      </div>
    </div>
    <div className='flex-none w-full sm:w-[170px]'>{children}</div>
  </div>
)

PreferenceRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  required: PropTypes.bool,
  description: PropTypes.string,
  children: PropTypes.node,
  isLast: PropTypes.bool
}

const PreferenceSelect = ({ value, onChange, options }) => {
  const current = options.find((o) => o.value === value)
  return (
    <Select value={value != null ? String(value) : ''} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className='h-10 w-full justify-between rounded-lg border-[#D1D5D8] bg-white px-3 text-[13px] font-medium text-[#0A1628] [&>span]:line-clamp-none [&>span]:whitespace-nowrap'>
        <SelectValue placeholder='—'>{current?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent className='max-h-[260px]'>
        {options.map((o) => (
          <SelectItem key={o.value} value={String(o.value)}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

PreferenceSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired
}

const EmailChipsInput = ({ value, onChange }) => {
  const [draft, setDraft] = useState('')
  const emails = (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  const commit = (raw) => {
    const next = raw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (next.length === 0) return
    const merged = Array.from(new Set([...emails, ...next]))
    onChange(merged.join(', '))
    setDraft('')
  }

  const remove = (email) => {
    onChange(emails.filter((e) => e !== email).join(', '))
  }

  return (
    <div className='flex flex-wrap items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-2 py-1.5 min-h-[40px]'>
      {emails.map((e) => (
        <span key={e} className='inline-flex items-center gap-1.5 rounded-md bg-[#F1F4F8] px-2 py-1 text-[12px] text-[#0A1628]'>
          <span className='text-[#475569]'>✉</span>
          {e}
          <button type='button' onClick={() => remove(e)} aria-label={`Remove ${e}`} className='text-[#6B7280] hover:text-[#0A1628]'>
            <X className='h-3 w-3' />
          </button>
        </span>
      ))}
      <input
        type='text'
        value={draft}
        onChange={(ev) => setDraft(ev.target.value)}
        onBlur={() => draft && commit(draft)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter' || ev.key === ',') {
            ev.preventDefault()
            commit(draft)
          } else if (ev.key === 'Backspace' && !draft && emails.length) {
            remove(emails[emails.length - 1])
          }
        }}
        placeholder={emails.length === 0 ? 'Add email...' : 'Add email...'}
        className='flex-1 min-w-[140px] bg-transparent text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:outline-none'
      />
    </div>
  )
}

EmailChipsInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

const CalendarPreferences = ({ initialSettings, setBookingLinkOpen, onSuccess, onRegisterSave }) => {
  const organisationId = useOrganisationStore((state) => state.organisationId)
  const organisationDetails = useOrganisationStore((state) => state.organisationDetail)
  const ccId = useCenterStore((state) => state.ccId)
  const setLoading = useLoadingStore((state) => state.setLoading)
  const [isSaveEnabled, setIsSaveEnabled] = useState(false)
  const [open, setOpen] = useState(false)

  const convertDaysToMinutes = (days) => dayjs.duration({ days }).asMinutes()
  const convertMinutesToDays = (minutes) => dayjs.duration(minutes, 'minutes').asDays()

  const mapOrganisationDetails = (details) => ({
    technicians: details.available_tech,
    duration: details.appointment_duration,
    minTimeBeforeBooking: details.min_time,
    maxDaysAdvance: convertMinutesToDays(details.max_time),
    minHoursBeforeReschedule: details.min_update_time,
    notificationRecipients: details.notification_recipients,
    startTimeIntervals: details.timeslot_gap
  })

  const [settings, setSettings] = useState(() =>
    organisationDetails ? { ...initialSettings, ...mapOrganisationDetails(organisationDetails) } : initialSettings
  )

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }))
    setIsSaveEnabled(true)
  }

  const buildPayload = () => ({
    id: organisationId,
    available_tech: settings.technicians,
    appointment_duration: settings.duration,
    appointment_gap: 0,
    min_time: settings.minTimeBeforeBooking,
    max_time: convertDaysToMinutes(settings.maxDaysAdvance),
    min_update_time: settings.minHoursBeforeReschedule,
    notification_recipients: settings.notificationRecipients,
    timeslot_gap: settings.startTimeIntervals
  })

  const handleSubmit = async () => {
    try {
      setLoading(true)
      const response = await createCalendarPreferences(ccId, buildPayload())
      if (response?.status) {
        setOpen(true)
        setIsSaveEnabled(false)
        onSuccess && onSuccess()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { path: 'createCalendarPreferences' } })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = (event, value) => {
    if (value && value === 'backdropClick') return
    setOpen(false)
    setBookingLinkOpen(false)
  }
  const viewBookingPage = (event, value) => {
    if (value && value === 'backdropClick') return
    setOpen(false)
    setBookingLinkOpen(true)
  }

  useEffect(() => {
    if (!onRegisterSave) return
    onRegisterSave({ onSave: handleSubmit, disabled: !isSaveEnabled })
  }, [settings, isSaveEnabled, onRegisterSave])

  return (
    <div className='space-y-8'>
      <section>
        <div className='mb-3'>
          <h2 className='text-[18px] font-bold text-[#0A1628]'>Booking Rules</h2>
          <p className='mt-0.5 text-[13px] text-[#6B7280]'>How time slots are sized, spaced, and offered to clients.</p>
        </div>
        <div className='rounded-xl border border-[#EBEDF0] bg-white'>
          <PreferenceRow
            icon={Users}
            title='Technicians'
            required
            description='Number of available technicians. Multiple appointments can be scheduled simultaneously up to this number.'
          >
            <PreferenceSelect value={settings.technicians} onChange={(v) => handleChange('technicians', v)} options={techniciansOptions} />
          </PreferenceRow>
          <PreferenceRow
            icon={Clock}
            title='Duration of Appointment'
            description='Default length of time reserved for each calibration appointment.'
          >
            <PreferenceSelect value={settings.duration} onChange={(v) => handleChange('duration', v)} options={durationOptions} />
          </PreferenceRow>
          <PreferenceRow
            icon={Clock}
            title='Start Every'
            description='How often appointment slots are available externally. A 90-minute interval starts slots at 8:00, 9:30, 11:00, etc.'
          >
            <PreferenceSelect
              value={settings.startTimeIntervals}
              onChange={(v) => handleChange('startTimeIntervals', v)}
              options={timeIntervalOptions}
            />
          </PreferenceRow>
          <PreferenceRow
            icon={Clock}
            title='Minimum Time Before Booking'
            description='Minimum lead time required before an appointment can be scheduled.'
          >
            <PreferenceSelect
              value={settings.minTimeBeforeBooking}
              onChange={(v) => handleChange('minTimeBeforeBooking', v)}
              options={timeGapOptions}
            />
          </PreferenceRow>
          <PreferenceRow icon={CalendarIcon} title='Maximum Days in Advance' description='How far ahead clients can book online.'>
            <PreferenceSelect value={settings.maxDaysAdvance} onChange={(v) => handleChange('maxDaysAdvance', v)} options={daysOptions} />
          </PreferenceRow>
          <PreferenceRow
            icon={Clock}
            title='Minimum Time Before Reschedule'
            description='Minimum lead time before an appointment can be rescheduled or canceled by the external client.'
            isLast
          >
            <PreferenceSelect
              value={settings.minHoursBeforeReschedule}
              onChange={(v) => handleChange('minHoursBeforeReschedule', v)}
              options={minRescheduleOptions}
            />
          </PreferenceRow>
        </div>
      </section>

      <section>
        <div className='mb-3'>
          <h2 className='text-[18px] font-bold text-[#0A1628]'>Notifications</h2>
          <p className='mt-0.5 text-[13px] text-[#6B7280]'>
            People who get an email when an appointment is booked, rescheduled, or canceled.
          </p>
        </div>
        <div className='rounded-xl border border-[#EBEDF0] bg-white'>
          <div className='flex flex-col gap-3 px-4 py-4'>
            <div className='flex items-start gap-3'>
              <span className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-md bg-[#E8EFFF]'>
                <Bell className='h-4 w-4 text-[#1F6FEB]' />
              </span>
              <div>
                <div className='text-[14px] font-bold text-[#0A1628]'>Notification Recipients</div>
                <div className='mt-0.5 text-[12px] text-[#6B7280]'>
                  Choose the people who should receive these emails. Separate multiple addresses with commas.
                </div>
              </div>
            </div>
            <EmailChipsInput value={settings.notificationRecipients} onChange={(v) => handleChange('notificationRecipients', v)} />
          </div>
        </div>
      </section>

      {open && (
        <FormModal
          open={open}
          onClose={handleClose}
          title='Your Appointment Schedule is Live'
          icon={CheckCircle2}
          width='max-w-[480px]'
          bodyClassName='py-5'
          footer={
            <AdasBox className='flex flex-col justify-end gap-3 sm:flex-row'>
              <button
                type='button'
                onClick={viewBookingPage}
                className='inline-flex items-center justify-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
              >
                <Link className='h-4 w-4' />
                View Booking Page
              </button>
              <button
                type='button'
                onClick={handleClose}
                className='inline-flex items-center justify-center rounded-lg bg-sidebar px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sidebar/90'
              >
                Okay
              </button>
            </AdasBox>
          }
        >
          <p className='text-[14px] leading-relaxed text-[#475569]'>
            Your changes have been saved. Clients can now book appointments using your direct booking link or the embedded scheduler.
          </p>
        </FormModal>
      )}
    </div>
  )
}

CalendarPreferences.propTypes = {
  initialSettings: PropTypes.shape({
    technicians: PropTypes.number,
    startTimeIntervals: PropTypes.number,
    duration: PropTypes.number,
    gap: PropTypes.number,
    minTimeBeforeBooking: PropTypes.number,
    maxDaysAdvance: PropTypes.number,
    minHoursBeforeReschedule: PropTypes.number,
    notificationRecipients: PropTypes.string
  }),
  setBookingLinkOpen: PropTypes.func,
  onSuccess: PropTypes.func,
  onRegisterSave: PropTypes.func
}

CalendarPreferences.defaultProps = {
  initialSettings: {
    technicians: 2,
    startTimeIntervals: 30,
    duration: 90,
    gap: 0,
    minTimeBeforeBooking: 0,
    maxDaysAdvance: 14,
    minHoursBeforeReschedule: 30,
    notificationRecipients: ''
  }
}

export default CalendarPreferences
